/**
 * ReferenceCatalogList — generic list view для reference-CRUD экранов
 * Platform Admin (AK-231 Pilot). Каркас:
 *
 *   header (title + subtitle + primary CTA)
 *   card { toolbar (search + filter slots) → optional error → table → empty → pagination? }
 *
 * Конкретный справочник подсказывает:
 *   - columns: что рендерить в каждой строке;
 *   - toolbarSlot: какие фильтры показать рядом с поиском;
 *   - emptyHint: что писать в empty-state;
 *   - rowActions: kebab-меню действий.
 *
 * Цель — переиспользование для остальных 7 справочников эпика AK-226
 * (Болезни, Вредители, Удобрения, Препараты СЗР, Сорняки, Гибриды, Стадии).
 */
import { type ReactNode } from 'react';
import { AlertCircle, Plus } from 'lucide-react';
import { BrandIcon } from '@/components/ui/brand-icon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export interface ReferenceColumn<T> {
  key: string;
  header: ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
  render: (row: T) => ReactNode;
}

export interface ReferenceCatalogListProps<T> {
  title: string;
  subtitle?: string;
  /** Лейбл кнопки «Создать …» в header. */
  createLabel: string;
  onCreate: () => void;

  /** Поиск. Контролируется родителем (как в Companies.tsx). */
  search: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder?: string;

  /** Слот для дополнительных фильтров справа от поиска (Select / toggles). */
  toolbarSlot?: ReactNode;

  /** Данные. */
  items: T[];
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  onRetry?: () => void;
  getRowKey: (row: T) => string;

  columns: ReferenceColumn<T>[];

  emptyTitle?: string;
  emptyHint?: string;

  /**
   * Подгрузка порциями (AK-574). До неё справочники запрашивали фикс. окно
   * (100 культур / 200 болезней) при честном `total` с сервера: «найдено 340»
   * в подзаголовке и 200 строк в таблице, без единого намёка на обрыв.
   *
   * Семантика повторяет `VirtualTable` (AK-348, эталон бесконечных списков):
   * компонент только просит `onLoadMore()`, курсор держит вызывающая сторона.
   * Виртуализации здесь нет намеренно — справочники это сотни строк, а не
   * десятки тысяч, и переезд на grid-разметку VirtualTable ломает существующие
   * ячейки-редакторы. Когда каталог перевалит за тысячи — переезжать туда.
   */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Всего записей по фильтру (с сервера). Нужен для «показано N из M». */
  total?: number;
  /** Единица счётчика: «культур», «болезней». */
  unitLabel?: string;
}

const ALIGN_CLASS: Record<NonNullable<ReferenceColumn<unknown>['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

export function ReferenceCatalogList<T>({
  title,
  subtitle,
  createLabel,
  onCreate,
  search,
  onSearchChange,
  searchPlaceholder = 'Поиск…',
  toolbarSlot,
  items,
  isLoading,
  isError,
  error,
  onRetry,
  getRowKey,
  columns,
  emptyTitle = 'Записи не найдены',
  emptyHint,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  total,
  unitLabel = 'записей',
}: ReferenceCatalogListProps<T>) {
  const isEmpty = !isLoading && !isError && items.length === 0;
  const showFooter =
    !isEmpty && !isLoading && (hasMore || (total !== undefined && total > 0));

  return (
    <>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          {subtitle && (
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <Button onClick={onCreate}>
          <Plus className="h-4 w-4" />
          {createLabel}
        </Button>
      </div>

      <Card className="mt-5">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="relative flex-1 min-w-[220px]">
            <BrandIcon
              name="search"
              size={16}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-8"
            />
          </div>
          {toolbarSlot}
        </div>

        {/* Error banner */}
        {isError && (
          <div className="flex items-start gap-2 border-b border-border bg-destructive/5 px-4 py-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1">
              <div className="font-medium">Не удалось загрузить данные</div>
              <div className="text-xs opacity-80">
                {error?.message ?? 'Неизвестная ошибка'}
              </div>
            </div>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                Повторить
              </Button>
            )}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHead
                    key={col.key}
                    style={col.width ? { width: col.width } : undefined}
                    className={col.align ? ALIGN_CLASS[col.align] : undefined}
                  >
                    {col.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && items.length === 0
                ? Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {columns.map((col) => (
                        <TableCell key={col.key}>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : items.map((row) => (
                    <TableRow key={getRowKey(row)}>
                      {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          className={col.align ? ALIGN_CLASS[col.align] : undefined}
                        >
                          {col.render(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>

        {/* Empty state */}
        {isEmpty && (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <div className="text-sm font-medium text-foreground">{emptyTitle}</div>
            {emptyHint && (
              <div className="text-xs text-muted-foreground max-w-md">
                {emptyHint}
              </div>
            )}
            <div className="mt-3">
              <Button size="sm" onClick={onCreate}>
                <Plus className="h-4 w-4" />
                {createLabel}
              </Button>
            </div>
          </div>
        )}

        {/* Подгрузка порциями + честный счётчик «показано N из M» */}
        {showFooter && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
            <span className="text-xs text-muted-foreground">
              {total === undefined
                ? `Показано ${items.length}`
                : `Показано ${items.length} из ${total} ${unitLabel}`}
            </span>
            {hasMore && onLoadMore && (
              <Button
                variant="outline"
                size="sm"
                onClick={onLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Загружаем…' : 'Показать ещё'}
              </Button>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
